import { useState, type ElementType } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  MapPin, Clock, CheckCircle2, Navigation, AlertTriangle,
  Briefcase, CalendarClock, TrendingUp, Plus, Users, Map as MapIcon,
  CalendarDays, Receipt, Sparkles, Loader2, Lightbulb,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext.js';
import { onsiteApiFetch } from '../config/api.js';
import { formatDate, formatTime } from '../lib/utils.js';
import type { OnsiteJob } from '../../../api/src/types/onsite-types.js';

interface DashboardStats {
  total: number;
  today: number;
  overdue: number;
  unscheduled?: number;
  open: number;
  active_techs_today?: number;
  completed_this_week: number;
  by_status: Record<string, number>;
  by_type: Record<string, number>;
  by_priority: Record<string, number>;
}

interface DashboardInsight {
  text: string;
  impact?: 'low' | 'medium' | 'high';
  action?: string;
  icon?: string;
}

interface InvoiceSummary {
  id: string;
  invoice_number: string;
  job_title: string;
  client_name: string;
  total: number;
  status: 'draft' | 'sent' | 'paid';
  issued_at: string;
}

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  new:        { label: 'New',         color: 'bg-purple-500/20 text-purple-400'  },
  scheduled:  { label: 'Scheduled',   color: 'bg-blue-500/20 text-blue-400'     },
  dispatched: { label: 'Dispatched',  color: 'bg-cyan-500/20 text-cyan-400'     },
  en_route:   { label: 'En Route',    color: 'bg-amber-500/20 text-amber-400'   },
  on_site:    { label: 'On Site',     color: 'bg-green-500/20 text-green-400'   },
  completed:  { label: 'Completed',   color: 'bg-muted/20 text-muted-foreground'   },
  invoiced:   { label: 'Invoiced',    color: 'bg-emerald-500/20 text-emerald-400' },
  closed:     { label: 'Closed',      color: 'bg-muted/20 text-muted-foreground'   },
  cancelled:  { label: 'Cancelled',   color: 'bg-red-500/20 text-red-400'       },
};

const INVOICE_BADGE: Record<string, string> = {
  draft: 'bg-yellow-500/20 text-yellow-400',
  sent:  'bg-blue-500/20 text-blue-400',
  paid:  'bg-green-500/20 text-green-400',
};

export default function DashboardPage() {
  const { user, orgId, isDispatcher } = useAuth();
  const today = new Date().toISOString().substring(0, 10);
  const [insights, setInsights] = useState<DashboardInsight[] | null>(null);
  const [insightsError, setInsightsError] = useState<string | null>(null);

  const { data: statsData } = useQuery({
    queryKey: ['jobs', 'stats', orgId],
    queryFn: () => onsiteApiFetch<DashboardStats>('/api/v1/onsite/jobs/stats'),
    enabled: !!orgId,
  });

  const { data: todayData, isLoading } = useQuery({
    queryKey: ['jobs', 'today', orgId],
    queryFn: () => onsiteApiFetch<{ jobs: OnsiteJob[] }>(
      `/api/v1/onsite/jobs?date=${today}${!isDispatcher ? `&tech_id=${user?.id}` : ''}`
    ),
    enabled: !!orgId && !!user,
  });

  const { data: invoicesData } = useQuery({
    queryKey: ['invoices', 'recent', orgId],
    queryFn: async () => {
      try {
        return await onsiteApiFetch<{ invoices: InvoiceSummary[] }>(
          '/api/v1/onsite/billing/invoices?limit=5&sort=recent'
        );
      } catch {
        return { invoices: [] };
      }
    },
    enabled: !!orgId,
  });

  const fetchInsights = useMutation({
    mutationFn: () =>
      onsiteApiFetch<{ insights: DashboardInsight[] }>(
        '/api/v1/onsite/reports/insights',
        { method: 'POST' }
      ),
    onSuccess: d => { setInsights(d.insights ?? []); setInsightsError(null); },
    onError: e => setInsightsError((e as Error).message),
  });

  const jobs = todayData?.jobs ?? [];
  const activeJob = jobs.find(j => j.status === 'on_site' || j.status === 'en_route');
  const upcoming = jobs.filter(j => j.status === 'scheduled' || j.status === 'dispatched' || j.status === 'new');
  const completed = jobs.filter(j => j.status === 'completed' || j.status === 'invoiced' || j.status === 'closed');
  const stats = statsData;
  const invoices = invoicesData?.invoices ?? [];

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto p-6">
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">
            Good {getGreeting()}, {user?.name.split(' ')[0]}
          </h1>
          <p className="text-muted-foreground">
            {formatDate(new Date().toISOString())} -- {jobs.length} job{jobs.length !== 1 ? 's' : ''} today
          </p>
        </div>
        <Link
          to="/jobs/new"
          className="flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" />
          New Job
        </Link>
      </div>

      {/* Quick Actions */}
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <QuickAction to="/jobs/new" icon={Plus} label="New Job" color="text-primary" />
        <QuickAction to="/dispatch" icon={CalendarClock} label="Open Dispatch" color="text-cyan-400" />
        <QuickAction to="/calendar" icon={CalendarDays} label="View Calendar" color="text-blue-400" />
        <QuickAction to="/map" icon={MapIcon} label="View Map" color="text-green-400" />
      </div>

      {/* Stats */}
      <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
        <StatCard label="Open Jobs" value={stats?.open ?? upcoming.length} icon={Briefcase} color="text-blue-400" />
        <StatCard label="Today" value={stats?.today ?? jobs.length} icon={CalendarClock} color="text-primary" />
        <StatCard
          label="Overdue"
          value={stats?.overdue ?? 0}
          icon={AlertTriangle}
          color={stats?.overdue ? 'text-red-400' : 'text-muted-foreground'}
          linkTo="/jobs?filter=overdue"
        />
        <StatCard label="Done This Week" value={stats?.completed_this_week ?? completed.length} icon={TrendingUp} color="text-green-400" />
      </div>

      {/* Extra KPIs */}
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard
          label="Unscheduled Jobs"
          value={stats?.unscheduled ?? stats?.by_status?.new ?? 0}
          icon={CalendarClock}
          color="text-amber-400"
          linkTo="/jobs?filter=unscheduled"
        />
        <StatCard
          label="Active Techs Today"
          value={stats?.active_techs_today ?? 0}
          icon={Users}
          color="text-cyan-400"
        />
        <StatCard
          label="Invoiced"
          value={stats?.by_status?.invoiced ?? 0}
          icon={Receipt}
          color="text-emerald-400"
          linkTo="/invoices"
        />
      </div>

      {/* Active job */}
      {activeJob && (
        <section className="mb-6">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-primary">
            Active Job
          </h2>
          <JobCard job={activeJob} highlighted />
        </section>
      )}

      {/* Upcoming */}
      {upcoming.length > 0 && (
        <section className="mb-6">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            Upcoming
          </h2>
          <div className="space-y-3">
            {upcoming.map(job => <JobCard key={job.id} job={job} />)}
          </div>
        </section>
      )}

      {/* Completed */}
      {completed.length > 0 && (
        <section className="mb-6">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            Completed Today
          </h2>
          <div className="space-y-3">
            {completed.map(job => <JobCard key={job.id} job={job} />)}
          </div>
        </section>
      )}

      {/* Recent Invoices */}
      <section className="mb-6">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            Recent Invoices
          </h2>
          <Link to="/invoices" className="text-xs text-primary hover:underline">View all</Link>
        </div>
        {invoices.length === 0 ? (
          <div className="rounded-xl border border-border bg-card p-4 text-sm text-muted-foreground">
            No invoices yet. Complete a job to generate your first invoice.
          </div>
        ) : (
          <div className="space-y-2">
            {invoices.slice(0, 5).map(inv => (
              <Link
                key={inv.id}
                to="/invoices"
                className="flex items-center gap-3 rounded-xl border border-border bg-card p-3 hover:bg-secondary/50"
              >
                <Receipt className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">
                    {inv.invoice_number} · {inv.job_title}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">{inv.client_name}</p>
                </div>
                <span className="text-sm font-semibold text-foreground">${inv.total.toFixed(2)}</span>
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${INVOICE_BADGE[inv.status] ?? INVOICE_BADGE.draft}`}>
                  {inv.status}
                </span>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* AI Insights */}
      <section className="mb-6">
        <div className="rounded-xl border border-primary/30 bg-primary/5 p-4">
          <div className="flex items-center justify-between gap-2 mb-2">
            <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <Sparkles className="h-4 w-4 text-primary" />
              AI Insights
            </h2>
            <button
              onClick={() => fetchInsights.mutate()}
              disabled={fetchInsights.isPending}
              className="flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {fetchInsights.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
              Get AI insights
            </button>
          </div>
          {insightsError && (
            <p className="text-sm text-red-400">AI assistant unavailable: {insightsError}</p>
          )}
          {insights && insights.length > 0 && (
            <ul className="space-y-2">
              {insights.map((ins, i) => (
                <li key={i} className="flex items-start gap-2 rounded-lg border border-border bg-card p-2">
                  <Lightbulb className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-foreground">{ins.text}</p>
                    {ins.action && <p className="mt-0.5 text-xs text-muted-foreground">{ins.action}</p>}
                  </div>
                  {ins.impact && (
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium uppercase ${
                      ins.impact === 'high' ? 'bg-red-500/20 text-red-400'
                      : ins.impact === 'medium' ? 'bg-amber-500/20 text-amber-400'
                      : 'bg-blue-500/20 text-blue-400'
                    }`}>
                      {ins.impact}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
          {insights && insights.length === 0 && (
            <p className="text-sm text-muted-foreground">No insights right now — check back after more data is collected.</p>
          )}
        </div>
      </section>

      {jobs.length === 0 && (
        <div className="flex flex-col items-center justify-center gap-3 py-20 text-muted-foreground">
          <CheckCircle2 className="h-12 w-12" />
          <p className="text-lg font-medium">No jobs scheduled today</p>
          <p className="text-sm">
            {isDispatcher ? 'Create a new job to get started' : 'Check with your dispatcher for updates'}
          </p>
          {isDispatcher && (
            <Link
              to="/jobs/new"
              className="mt-2 flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              <Plus className="h-4 w-4" />
              Create Job
            </Link>
          )}
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color, linkTo }: {
  label: string; value: number; icon: ElementType; color: string; linkTo?: string;
}) {
  const card = (
    <div className="rounded-xl border border-border bg-card p-4 transition-colors hover:border-primary/30">
      <div className={`mb-1 ${color}`}>
        <Icon className="h-5 w-5" />
      </div>
      <p className="text-2xl font-bold text-foreground">{value}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  );
  return linkTo ? <Link to={linkTo}>{card}</Link> : card;
}

function QuickAction({ to, icon: Icon, label, color }: {
  to: string; icon: ElementType; label: string; color: string;
}) {
  return (
    <Link
      to={to}
      className="flex items-center gap-3 rounded-xl border border-border bg-card p-3 transition-colors hover:border-primary/40 hover:bg-secondary/50"
    >
      <Icon className={`h-5 w-5 ${color}`} />
      <span className="text-sm font-medium text-foreground">{label}</span>
    </Link>
  );
}

function JobCard({ job, highlighted = false }: { job: OnsiteJob; highlighted?: boolean }) {
  const status = STATUS_CONFIG[job.status] ?? STATUS_CONFIG.new;
  return (
    <Link
      to={`/jobs/${job.id}`}
      className={`block rounded-xl border p-4 transition-colors hover:bg-card/80 ${
        highlighted
          ? 'border-primary/40 bg-primary/10'
          : 'border-border bg-card'
      }`}
    >
      <div className="mb-2 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="font-semibold text-foreground line-clamp-1">{job.title}</h3>
          {job.job_type && job.job_type !== 'other' && (
            <span className="text-xs text-muted-foreground capitalize">{job.job_type.replace('_', ' ')}</span>
          )}
        </div>
        <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${status.color}`}>
          {status.label}
        </span>
      </div>
      <p className="mb-2 flex items-center gap-1.5 text-sm text-muted-foreground">
        <MapPin className="h-3.5 w-3.5 shrink-0" />
        {job.customer_name}{job.customer_address ? ` \u00B7 ${job.customer_address}` : ''}
      </p>
      <div className="flex items-center justify-between">
        <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <Clock className="h-3.5 w-3.5" />
          {formatTime(job.scheduled_start)} - {formatTime(job.scheduled_end)}
        </p>
        {job.customer_address && (
          <a
            href={`https://maps.apple.com/?q=${encodeURIComponent(job.customer_address)}`}
            target="_blank"
            rel="noreferrer"
            onClick={e => e.stopPropagation()}
            className="flex items-center gap-1 rounded px-2 py-1 text-xs text-primary hover:bg-primary/10"
          >
            <Navigation className="h-3 w-3" />
            Directions
          </a>
        )}
      </div>
    </Link>
  );
}

function getGreeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'morning';
  if (h < 17) return 'afternoon';
  return 'evening';
}
